/**
 * 旧路径 → 新路径（总纲 §6「旧路径全部重定向」）。
 *
 * 纯函数，不碰 React：路由表用它做 `<Navigate replace>`，测试直接枚举表里每一条。
 * 员工页 18 个旧 tab 各自落到三面之一 + 锚点，书签与帮助文档里的旧链接不会断。
 */

/** 员工主页三面（总纲 §2.3）。 */
export type EmployeeFace = 'report' | 'lists' | 'brief';

export const EMPLOYEE_FACES: readonly EmployeeFace[] = ['report', 'lists', 'brief'];

/**
 * 旧 tab → 面 + 锚点。锚点是「交代」页「更多」折叠里的 section id，
 * 带着锚点进来会自动展开并滚到那一段。
 */
export const LEGACY_EMPLOYEE_TABS: Readonly<
  Record<string, { face: EmployeeFace; anchor?: string }>
> = {
  dashboard: { face: 'report' },
  'dial-progress': { face: 'report', anchor: 'progress' },
  'lead-details': { face: 'lists', anchor: 'numbers' },
  'upload-leads': { face: 'lists', anchor: 'give' },
  'lead-settings': { face: 'brief', anchor: 'customer-fields' },
  'lead-schema': { face: 'brief', anchor: 'customer-fields' },
  'test-call': { face: 'brief', anchor: 'test-call' },
  profile: { face: 'brief', anchor: 'profile' },
  script: { face: 'brief', anchor: 'script' },
  voice: { face: 'brief', anchor: 'voice' },
  line: { face: 'brief', anchor: 'line' },
  'dial-policy': { face: 'brief', anchor: 'schedule' },
  inbound: { face: 'brief', anchor: 'inbound' },
  memory: { face: 'brief', anchor: 'memory' },
  push: { face: 'brief', anchor: 'push' },
  sms: { face: 'brief', anchor: 'sms' },
  wechat: { face: 'brief', anchor: 'wechat' },
  integration: { face: 'brief', anchor: 'api' },
  share: { face: 'brief', anchor: 'share' },
};

/** 整页级旧路径 → 新路径。查询串原样带过去（筛选、分页不丢）。 */
export const LEGACY_PATHS: Readonly<Record<string, string>> = {
  '/catalog': '/employees/hire',
  '/leads': '/customers/leads',
  '/blacklist': '/customers/leads?view=blocked',
  '/campaigns': '/work',
  '/data/report': '/today?range=7d',
  '/data/calls': '/customers/calls',
  '/data/intentions': '/customers/intents',
  '/resources/lines': '/settings/resources/lines',
  '/resources/sms': '/settings/resources/sms',
  '/resources/wechat': '/settings/resources/wechat',
  '/tenant/certification': '/settings/company',
  '/tenant/members': '/settings/members',
  '/finance/capital': '/billing',
  '/finance/seats': '/billing/seats',
  '/finance/settlement': '/billing/bills',
  '/finance/invoices': '/billing/invoices',
  '/system/api-keys': '/settings/developer',
  '/system/exports': '/settings/exports',
  '/system/logs': '/settings/logs',
  '/system/messages': '/inbox',
};

function mergeSearch(target: string, search: string): string {
  if (!search || search === '?') return target;
  const extra = search.startsWith('?') ? search.slice(1) : search;
  return target.includes('?') ? `${target}&${extra}` : `${target}?${extra}`;
}

/**
 * 把一个旧地址解析成新地址；不是旧地址返回 null。
 * @param pathname 不含查询串与 hash
 * @param search   `location.search`（可空）
 */
export function resolveLegacyPath(pathname: string, search = ''): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const direct = LEGACY_PATHS[clean];
  if (direct) return mergeSearch(direct, search);

  const m = /^\/employees\/([^/]+)\/([^/]+)$/.exec(clean);
  if (m) {
    const [, id, tab] = m;
    if (id === 'hire' || EMPLOYEE_FACES.includes(tab as EmployeeFace)) return null;
    const hit = LEGACY_EMPLOYEE_TABS[tab as string];
    if (!hit) return null;
    const base = `/employees/${id}/${hit.face}`;
    return mergeSearch(base, search) + (hit.anchor ? `#${hit.anchor}` : '');
  }
  return null;
}
